import {
  getApp,
  initializeApp,
  type FirebaseApp,
  type FirebaseOptions
} from 'firebase/app'
import {
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  getAuth,
  inMemoryPersistence,
  initializeAuth,
  onIdTokenChanged,
  signInWithPopup,
  signOut,
  type Auth,
  type Persistence,
  type PopupRedirectResolver,
  type User
} from 'firebase/auth'

import { env } from '../config/env.ts'
import { createInternalAuth } from '../lib/create-auth.ts'
import { IDLE_LIMIT_MS } from '../lib/idle.ts'
import {
  decodeIdToken,
  isExpired,
  isRestorable,
  type IdTokenClaims
} from './id-token.ts'
import {
  SESSION_KEY,
  clearSession,
  parseSession,
  readSession,
  writeIdToken,
  writeLastActivity,
  type StoredSession
} from './session-store.ts'

/**
 * The signed-in principal this app keeps in React state.
 *
 * `uid` and `email` come off the live Firebase user while one exists, and off
 * the stored ID token's `sub` and `email` claims after a reload, when there is
 * no Firebase user at all.
 */
export interface InternalUser {
  uid: string
  email: string | null
}

let cached: Auth | undefined

/** The App's subscriber, so a sign-out can reach it with no Firebase event. */
let listener: ((user: InternalUser | null) => void) | null = null

/**
 * The session rebuilt from the store, held until Firebase supplies a live one.
 *
 * It is what tells a `null` from Firebase apart from a sign-out.
 */
let restored: InternalUser | null = null

let expiryTimer: ReturnType<typeof setTimeout> | undefined

/**
 * Bumped by every sign-out.
 *
 * `persistToken` awaits the SDK, so a sign-out can land while it is in flight.
 * Writing after that would put the bearer token back on disk and a reload
 * would restore the session the operator just ended.
 */
let epoch = 0

/** Whether the restore has been attempted for this page load. */
let restoreAttempted = false

/**
 * Whether this tab believes it holds a session.
 *
 * A Firebase `null` means different things before and after a live session:
 * "nobody is signed in yet" the first time, and "the session was revoked" once
 * one has existed. Only the second must clear the store.
 */
let live = false

/**
 * Whether a sign-out has happened in this tab.
 *
 * A refresh already in flight elsewhere can write the key back a moment after
 * a sign-out cleared it, and this tab would otherwise adopt the session it
 * just ended. Cleared again by a real sign-in here.
 */
let signedOutHere = false

function toInternalUser(user: User | null): InternalUser | null {
  if (!user) {
    return null
  }

  return { uid: user.uid, email: user.email }
}

/**
 * The Firebase Auth instance for this page lifetime.
 *
 * `inMemoryPersistence` at construction, so the SDK writes nothing and the
 * refresh token never leaves memory. What survives a reload is the ID token
 * this module stores itself — a different artefact (ADR-0003).
 *
 * @returns The tenant-pinned Auth instance.
 */
export function getInternalAuth(): Auth {
  if (!cached) {
    cached = createInternalAuth(env, {
      getApp,
      initializeApp: (config, name) =>
        initializeApp(config as FirebaseOptions, name),
      initializeAuth: (app, options) =>
        initializeAuth(app as FirebaseApp, {
          persistence: options.persistence as Persistence,
          popupRedirectResolver:
            options.popupRedirectResolver as PopupRedirectResolver
        }),
      inMemoryPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
      getAuth: app => getAuth(app as FirebaseApp)
    }) as Auth
  }

  return cached
}

/**
 * The largest delay `setTimeout` keeps. Anything above it is clamped to 1ms,
 * which would make a forged far-future `exp` fire at once and loop.
 */
const MAX_TIMEOUT_MS = 2_147_483_647

/** How long to wait before retrying a refresh that produced no usable token. */
const EXPIRY_RETRY_MS = 60_000

/**
 * End the session from an event rather than a user action.
 *
 * `signOutInternal` rethrows a Firebase failure for the UI to show. Nothing is
 * watching at these call sites, so the rejection would reach the page.
 */
function endSession(): void {
  void signOutInternal().catch(() => undefined)
}

function clearExpiryTimer(): void {
  if (expiryTimer !== undefined) {
    clearTimeout(expiryTimer)
    expiryTimer = undefined
  }
}

/**
 * End the session when the current token reaches its own `exp`.
 *
 * Re-armed each time a newer token arrives, so the console cannot sit looking
 * signed in behind a dead one.
 *
 * @param expiresAt - The token's expiry in milliseconds since the epoch.
 */
function armExpiry(expiresAt: number): void {
  clearExpiryTimer()
  const wait = Math.min(expiresAt - Date.now(), MAX_TIMEOUT_MS)
  if (wait <= 0) {
    onExpiry()

    return
  }

  expiryTimer = setTimeout(onExpiry, wait)
}

/**
 * Read the stored session, clearing the key when the token is unusable.
 *
 * Unusable means malformed or past its own `exp`, and nothing else. The key is
 * origin-wide, so a token another tab may still be refreshing must not be
 * deleted here — removing it signs every other tab out.
 *
 * @returns The stored session and its claims, or null.
 */
function readUsableSession(): {
  session: StoredSession
  claims: IdTokenClaims
} | null {
  const session = readSession()
  if (!session) {
    return null
  }

  const claims = decodeIdToken(session.idToken)
  if (!claims || isExpired(claims, Date.now())) {
    clearSession()

    return null
  }

  return { session, claims }
}

/**
 * The session this page load starts with, rebuilt from the stored ID token.
 *
 * Attempted once and memoised, so App can seed React state with it during its
 * first render. Without that the login screen paints for a frame on every
 * reload before the subscription's effect replaces it.
 *
 * @returns The restored principal, or null.
 */
export function initialSession(): InternalUser | null {
  if (!restoreAttempted) {
    restoreAttempted = true
    restored = restoreSession()
  }

  return restored
}

/**
 * The stored token has reached the expiry this tab armed against.
 *
 * The store is origin-wide, so what is on disk now may not be what the timer
 * was set from. Two escapes before ending anything, because ending clears the
 * key and the storage event would take every other tab with it.
 */
function onExpiry(): void {
  const user = getInternalAuth().currentUser
  if (user) {
    // This tab's own session lives in memory and can mint a fresh token. The
    // store is not authoritative for it.
    //
    // Re-armed first, and replaced by persistToken on success: a refresh that
    // throws or returns something unreadable must not leave this tab running
    // with no expiry at all.
    armExpiry(Date.now() + EXPIRY_RETRY_MS)
    void persistToken(user)

    return
  }

  const held = readUsableSession()
  if (held) {
    // Another tab refreshed the token since this timer was set.
    armExpiry(held.claims.expiresAt)

    return
  }

  endSession()
}

/**
 * Rebuild a session from the stored ID token.
 *
 * @returns The restored principal, or null.
 */
function restoreSession(): InternalUser | null {
  const held = readUsableSession()
  if (!held) {
    return null
  }

  // Too little life left to start a session from — but the token is still
  // valid, and a live tab may be about to refresh it. Refuse the restore and
  // leave the key where it is.
  if (!isRestorable(held.claims, Date.now())) {
    return null
  }

  // The idle allowance is the other half of the session's life, and it now
  // survives a reload too. Checked here rather than left to IdleGuard: the
  // guard ends the session on its first effect, but by then the console shell
  // and the router have already mounted.
  if (Date.now() - held.session.lastActivityAt >= IDLE_LIMIT_MS) {
    clearSession()

    return null
  }
  armExpiry(held.claims.expiresAt)

  return { uid: held.claims.uid, email: held.claims.email }
}

/**
 * Store the live session's current ID token.
 *
 * This is what makes the hour slide rather than run from sign-in: the SDK
 * refreshes while the tab is live and `onIdTokenChanged` fires on every
 * refresh, so the stored copy stays fresh.
 *
 * @param user - The live Firebase user.
 */
async function persistToken(user: User): Promise<void> {
  const startedAt = epoch
  let token: string
  try {
    token = await user.getIdToken()
  } catch {
    // Offline, or a revoked session. The live session still stands; only the
    // stored copy goes stale, and the next event refreshes it.
    return
  }

  if (startedAt !== epoch) {
    return
  }

  const claims = decodeIdToken(token)
  if (!claims) {
    // Not a JWT this app can read. Storing it would leave a row every reader
    // treats as unusable and deletes, and the deletion signs the other tabs
    // out. The row already on disk still matches the armed timer, so that
    // timer is left alone.
    return
  }

  if (isExpired(claims, Date.now())) {
    // Storing it would arm a timer due immediately, whose handler asks this
    // same live user for another token — without bound.
    endSession()

    return
  }

  writeIdToken(token)
  armExpiry(claims.expiresAt)
}

/**
 * Was the value a `storage` event removed a session still worth having?
 *
 * @param raw - The removed key contents.
 * @returns True when it held a token that had not expired.
 */
function wasUsable(raw: string): boolean {
  const session = parseSession(raw)
  if (!session) {
    return false
  }
  const claims = decodeIdToken(session.idToken)

  return claims !== null && !isExpired(claims, Date.now())
}

/**
 * Take up a session another tab has just written.
 *
 * Only for a tab with no live Firebase session of its own, which is
 * authoritative for its tab. A tab holding a restored session still adopts,
 * because the new row may be a different principal.
 *
 * @param onChange - The subscriber to tell.
 */
function adoptStoredSession(
  onChange: (user: InternalUser | null) => void
): void {
  if (signedOutHere || live || getInternalAuth().currentUser) {
    return
  }

  const before = restored?.uid
  restoreAttempted = false
  const adopted = initialSession()
  // Only on a change of principal, so another tab's five-second activity
  // stamps do not re-render this one — and so a sign-in as somebody else does.
  if (adopted && adopted.uid !== before) {
    onChange(adopted)
  }
}

/**
 * Subscribe to the session: the stored one first, then Firebase's.
 *
 * `onIdTokenChanged`, not `onAuthStateChanged`: only the former fires on a
 * silent refresh, and without that the stored copy goes stale and the session
 * dies early.
 *
 * @param onChange - Called with the current user, or null when signed out.
 * @returns Unsubscribe.
 */
export function subscribeToSession(
  onChange: (user: InternalUser | null) => void
): () => void {
  listener = onChange
  if (initialSession()) {
    onChange(restored)
  }

  // A sign-out in one tab has to end the session in every tab. With
  // `inMemoryPersistence` each tab holds its own Firebase session, so the SDK
  // cannot pass this along — the key going away is the only signal there is.
  // It fires in other tabs only, so this cannot recurse.
  const onStorage = (event: StorageEvent): void => {
    // A null key is a whole-store clear.
    if (event.key !== null && event.key !== SESSION_KEY) {
      return
    }

    // `newValue`, never a fresh read: the event says what this change did, so
    // a write landing behind it cannot hide a removal.
    if (event.newValue !== null) {
      // Another tab signed in. A tab sitting on the login screen next to a
      // live session should join it rather than wait to be reloaded.
      adoptStoredSession(onChange)

      return
    }

    // A removal alone does not say why. The event carries what was removed,
    // and that does: a **usable** token was taken away by a sign-out, while a
    // dead one was only tidied up. Following the second out would let a stale
    // tab end a live tab's still-refreshable session; ignoring the first would
    // leave a signed-out operator still signed in next door.
    const removed = event.oldValue
    if (removed !== null && !wasUsable(removed)) {
      return
    }

    endSession()
  }
  window.addEventListener('storage', onStorage)

  const unsubscribe = onIdTokenChanged(getInternalAuth(), user => {
    if (!user) {
      // After a reload Firebase has no user at all, so `null` here means
      // "nothing live to add", not "sign out".
      if (restored) {
        return
      }
      if (live) {
        // Firebase had a session and lost it — a revoke, or a refresh that
        // failed. Nothing may stay on disk, and an in-flight persistToken
        // must not write after this.
        live = false
        endSession()

        return
      }
      // Nothing has ever been live in this tab, and a restore would have
      // cleared anything unusable, so the store is already empty.
      onChange(null)

      return
    }

    // A live session always wins: it can refresh itself, a restored one
    // cannot.
    restored = null
    signedOutHere = false
    if (!live) {
      live = true
      // A new sign-in starts a fresh allowance. A row left on disk by an
      // earlier session can carry a spent activity stamp, and IdleGuard would
      // otherwise mount already expired and sign this session straight out.
      writeLastActivity(Date.now())
    }
    onChange(toInternalUser(user))
    void persistToken(user)
  })

  return () => {
    if (listener === onChange) {
      listener = null
    }
    window.removeEventListener('storage', onStorage)
    unsubscribe()
  }
}

/**
 * Start Google SSO against the configured tenant.
 *
 * `hd` only narrows the account chooser; it is not a control. The
 * Django auth class is what pins the Workspace domain.
 */
export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ hd: 'blytzpay.com' })
  await signInWithPopup(getInternalAuth(), provider)
}

/**
 * The current session's Identity Platform ID token.
 *
 * A live Firebase user is asked for a fresh one every time; the SDK refreshes
 * as needed. After a reload there is no Firebase user, so the stored token
 * answers — re-checked against its `exp` at the moment of use, and cleared
 * when dead.
 *
 * @returns The ID token, or null when nobody is signed in.
 */
export async function getInternalIdToken(): Promise<string | null> {
  const user = getInternalAuth().currentUser
  if (user) {
    return await user.getIdToken()
  }

  const held = readUsableSession()
  if (held) {
    return held.session.idToken
  }

  // The token was dead and has just been cleared. Every other tab learns that
  // from the storage event; this one has to be told directly, or it keeps
  // rendering a console behind a session that has ended.
  if (live || restored) {
    endSession()
  }

  return null
}

/**
 * End the session.
 *
 * The stored token goes first, so a `signOut` that throws still leaves nothing
 * usable behind. The subscriber is told directly: after a reload Firebase
 * holds no user, so `signOut` fires no event.
 */
export async function signOutInternal(): Promise<void> {
  epoch += 1
  live = false
  signedOutHere = true
  clearSession()
  restored = null
  clearExpiryTimer()

  try {
    await signOut(getInternalAuth())
  } finally {
    // Cleared a second time. A token refresh already in flight — here or in
    // another tab — can land between the first clear and this point, and Web
    // Storage has no compare-and-set to stop it. Clearing again once
    // everything has settled is the defence available.
    clearSession()
    listener?.(null)
  }
}
